import { z } from 'zod';

import { DEFAULT_SPREAD_TYPE } from 'watchtower-ui/components/uploadCSV/csvUtils';
import type { OptimizationAvailabilities } from 'watchtower-ui/pages/OptimizationPages/OptimizationTypeUtils';
import { formatDate, parseDateString } from 'watchtower-ui/utils/dateUtils';
import type { Unpacked } from 'watchtower-ui/utils/typeUtils';
import { asArray } from 'watchtower-ui/utils/utilityFunctions';

const filtersStepSchema = z
  .object({
    brands: z.array(z.string()).min(1, 'At least 1 brand must be selected'),
    parent_vehicle: z.array(z.string()).min(1, 'At least 1 parent vehicle must be selected'),
    dateRange: z.tuple([z.string(), z.string()]).nullable(),
    national: z.array(z.string()),
    state: z.array(z.string()).nullable(),
    region: z.array(z.string()).nullable(),
    wholesaler_number: z.array(z.string()).nullable(),
    dma: z.array(z.string()).nullable(),
    child_vehicles: z.record(z.string(), z.array(z.string()).nullable()),
  })
  .refine(
    (data) => {
      if (!data.dateRange) return false;
      const [startDate, endDate] = data.dateRange;
      if (!startDate || !endDate) return false;
      return new Date(endDate) >= new Date(startDate);
    },
    {
      message: 'End date must be equal to or greater than start date',
      path: ['dateRange'],
    },
  );

export type IFiltersStepForm = z.infer<typeof filtersStepSchema>;

export const getDefaultFiltersStepValues = (availabilities?: OptimizationAvailabilities): IFiltersStepForm => {
  const minDate = availabilities?.min_date ? parseDateString(availabilities.min_date) : new Date();
  const maxDate = availabilities?.max_date ? parseDateString(availabilities.max_date) : new Date();

  const defaultStartDate = availabilities?.default_date_range?.[0]
    ? parseDateString(availabilities.default_date_range[0])
    : minDate;
  const defaultEndDate = availabilities?.default_date_range?.[1]
    ? parseDateString(availabilities.default_date_range[1])
    : maxDate;

  const defaultDateRange: [string, string] = [
    defaultStartDate ? formatDate(defaultStartDate) : '',
    defaultEndDate ? formatDate(defaultEndDate) : '',
  ];

  const nationalOptions = asArray(availabilities?.geo?.national);
  const defaultNational =
    nationalOptions && Array.isArray(nationalOptions) && nationalOptions.length > 0 && nationalOptions[0]
      ? [nationalOptions[0]]
      : ['USA'];

  const regionOptions = asArray(availabilities?.geo?.region);
  const stateOptions = asArray(availabilities?.geo?.state);
  const childVehiclesEntries =
    asArray(availabilities?.signal?.parent_vehicle)?.map(
      (parent) => [parent, null] satisfies [string, string[] | null],
    ) ?? [];

  return {
    brands: [],
    parent_vehicle: [],
    dateRange: defaultDateRange,
    national: defaultNational,
    state: stateOptions && Array.isArray(stateOptions) && stateOptions.length > 0 ? stateOptions : null,
    region: regionOptions && Array.isArray(regionOptions) && regionOptions.length > 0 ? regionOptions : null,
    wholesaler_number: null,
    dma: null,
    child_vehicles: Object.fromEntries(childVehiclesEntries),
  };
};
const optimizeStepSchema = z
  .object({
    totalAmount: z
      .string()
      .min(1, 'Total amount is required')
      .refine((val) => {
        const num = parseFloat(val);
        return !Number.isNaN(num) && num > 0;
      }, 'Total amount must be greater than zero'),
    optimizeFor: z.string().min(1, 'Optimize for is required'),
    spendConstants: z.object({
      geo: z.object({
        isEnabled: z.boolean(),
        subCategory: z.string().optional(),
      }),
      investmentType: z
        .object({
          isEnabled: z.boolean(),
          subCategory: z.string().optional(),
        })
        .refine((data) => (data.isEnabled ? !!data.subCategory : true), {
          message: 'Subcategory is required when Investment Type is enabled',
          path: ['subCategory'],
        }),
      time: z.object({
        isEnabled: z.boolean(),
        subCategory: z.string().optional(),
      }),
    }),
    spread_type: z.string(),
  })
  .refine(
    (data) => {
      const anySpendConstantEnabled = Object.values(data.spendConstants).some((constant) => constant.isEnabled);
      return anySpendConstantEnabled ? !!data.spread_type : true;
    },
    {
      message: 'Spread type is required when shifting is enabled',
      path: ['spread_type'],
    },
  );

export type IOptimizeStepForm = z.infer<typeof optimizeStepSchema>;

export const getDefaultOptimizeStepValues = (): IOptimizeStepForm => ({
  totalAmount: '',
  optimizeFor: '$',
  spendConstants: {
    geo: { isEnabled: false, subCategory: undefined },
    investmentType: { isEnabled: false, subCategory: undefined },
    time: { isEnabled: false, subCategory: undefined },
  },
  spread_type: DEFAULT_SPREAD_TYPE,
});

export const RULE_VARIANTS = {
  GEOGRAPHY: 'geography',
  VEHICLE: 'vehicle',
  BRAND: 'brand',
} as const;

export const RuleVariantsList = [RULE_VARIANTS.GEOGRAPHY, RULE_VARIANTS.VEHICLE, RULE_VARIANTS.BRAND];

export const GEO_TYPES = {
  NATIONAL: 'national',
  REGION: 'region',
  STATE: 'state',
  DMA: 'dma',
} as const;

export const GeoTypesList = [GEO_TYPES.NATIONAL, GEO_TYPES.REGION, GEO_TYPES.STATE, GEO_TYPES.DMA];

const INVALID_SPEND_RANGE_MESSAGE = 'Min Spend cannot be greater than Max Spend.';

const parseSpendValue = (value?: string) => {
  if (!value?.trim()) return null;

  const normalizedValue = value.replaceAll(',', '').replaceAll('$', '').trim();
  const parsedValue = Number(normalizedValue);

  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const validateSpendRange = (
  data: {
    areSpendsEnabled?: boolean;
    minSpend?: string;
    maxSpend?: string;
  },
  ctx: z.RefinementCtx,
) => {
  if (!data.areSpendsEnabled) {
    return;
  }

  const minSpend = parseSpendValue(data.minSpend);
  const maxSpend = parseSpendValue(data.maxSpend);

  if (minSpend !== null && maxSpend !== null && minSpend > maxSpend) {
    ctx.addIssue({
      code: 'custom',
      message: INVALID_SPEND_RANGE_MESSAGE,
      path: ['minSpend'],
    });
  }
};

const childRuleCurveSchema = z
  .object({
    id: z.string(),
    vehicle: z.string().nullable().optional(),
    brands: z.array(z.string()).nullable().optional(),
    dateRange: z.tuple([z.string(), z.string()]).nullable(),
    minSpend: z.string().optional(),
    maxSpend: z.string().optional(),
    areSpendsEnabled: z.boolean().optional(),
    national: z.array(z.string()).nullable().optional(),
    state: z.array(z.string()).nullable().optional(),
    region: z.array(z.string()).nullable().optional(),
    wholesaler_number: z.array(z.string()).nullable().optional(),
    dma: z.array(z.string()).nullable().optional(),
  })
  .superRefine(validateSpendRange);

const ruleCurveSchema = z
  .object({
    id: z.string(),
    parent_vehicle: z.string().nullable()?.optional(),
    brands: z.array(z.string()).nullable()?.optional(),
    geo_spread: z.enum(GeoTypesList),
    national: z.array(z.string()).nullable().optional(),
    state: z.array(z.string()).nullable().optional(),
    region: z.array(z.string()).nullable().optional(),
    wholesaler_number: z.array(z.string()).nullable().optional(),
    dma: z.array(z.string()).nullable().optional(),
    dateRange: z.tuple([z.string(), z.string()]).nullable(),
    areSpendsEnabled: z.boolean().optional(),
    minSpend: z.string().optional(),
    maxSpend: z.string().optional(),
    children: z.array(childRuleCurveSchema).optional(),
  })
  .superRefine(validateSpendRange);

const rulesStepSchema = z.object({
  rulesVariant: z.enum(RuleVariantsList).optional().nullable(),
  rules: z.array(ruleCurveSchema).nullable(),
});

export type IRulesStepForm = z.infer<typeof rulesStepSchema>;

export type TypeRuleCurve = NonNullable<Unpacked<IRulesStepForm['rules']>>;

export type TypeChildRuleCurve = NonNullable<Unpacked<TypeRuleCurve['children']>>;

export const getDefaultRulesStepValues = (): IRulesStepForm => ({
  rulesVariant: null,
  rules: null,
});

export const getFieldOrder = (rulesVariant: (typeof RuleVariantsList)[number], ruleType: 'parent' | 'child') => {
  let vehicleFieldKey = 'vehicle';

  if (ruleType === 'parent') {
    vehicleFieldKey = 'parent_vehicle';
  }

  if (rulesVariant === RULE_VARIANTS.BRAND) {
    return ['brand', vehicleFieldKey, 'geo_spread'];
  }
  if (rulesVariant === RULE_VARIANTS.GEOGRAPHY) {
    return ['geo_spread', vehicleFieldKey, 'brand'];
  }
  return [vehicleFieldKey, 'brand', 'geo_spread'];
};

export const getValidGeoSpreadEntriesFromFilters = (filters: IFiltersStepForm) => {
  const geoSelectedEntries = Object.keys(filters || {})
    ?.filter((key) => GeoTypesList.includes(key as (typeof GeoTypesList)[number]))
    .map((key) => [key, filters[key as keyof IFiltersStepForm]])
    // Assertion as we are ensuring that value will be an array
    .filter(([, value]) => value && Array.isArray(value) && value.length > 0) as [
    TypeRuleCurve['geo_spread'],
    string[],
  ][];

  return geoSelectedEntries;
};

export { filtersStepSchema, optimizeStepSchema, rulesStepSchema };
